import {Banner} from '@wandb/components';
import {
  STATUS_INFO,
  StatusChip,
} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/common/StatusChip';
import {type TraceCallSchema} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientTypes';
import {
  flattenedTraceCallStatusCode,
  traceCallLatencyMs,
} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/tsDataModelHooks';
import React from 'react';

import {STATUS_VALUE_MAPPING, WEAVE_LATENCY_PATH} from '../constants';
import {type MetricType} from '../types';
import {getValue} from '../utils';

type ExampleValueProps = {
  metricPath: string;
  call: TraceCallSchema;
  metricType: MetricType;
};

const ExampleValue = ({metricPath, call, metricType}: ExampleValueProps) => {
  switch (metricType) {
    case 'status': {
      const status = flattenedTraceCallStatusCode(call);
      const statusNumericValue = STATUS_VALUE_MAPPING[status];
      return (
        <div className="flex items-center gap-8">
          <StatusChip value={status} iconOnly />
          <span className="text-moon-700 dark:text-wb-text-secondary">
            {STATUS_INFO[status]?.label}
          </span>
          <span className="text-wb-text-tertiary text-sm ml-8">
            (value: {statusNumericValue})
          </span>
        </div>
      );
    }

    case 'bool': {
      const boolValue = getValue(call, metricPath);
      return (
        <div className="text-sm text-wb-text-tertiary">{String(boolValue)}</div>
      );
    }

    case 'str': {
      const stringValue = getValue(call, metricPath);
      return (
        <div className="text-sm text-moon-500 dark:text-wb-text-tertiary">
          "{String(stringValue)}"
        </div>
      );
    }

    case 'float':
    default: {
      if (metricPath === WEAVE_LATENCY_PATH) {
        const ms = Math.round(traceCallLatencyMs(call));
        return (
          <div className="text-sm text-moon-500 dark:text-wb-text-tertiary">
            {ms}ms
          </div>
        );
      }

      const numericValue = getValue(call, metricPath);
      if (numericValue !== undefined && numericValue !== null) {
        return (
          <div className="text-sm text-wb-text-tertiary">
            {typeof numericValue === 'object'
              ? JSON.stringify(numericValue, null, 2)
              : String(numericValue)}
          </div>
        );
      }

      return (
        <div className="text-moon-400 dark:text-wb-text-tertiary italic">
          No example available
        </div>
      );
    }
  }
};

type ExampleMetricProps = {
  metricPath: string;
  call: TraceCallSchema;
  metricType: MetricType;
};

export const ExampleMetric = ({
  metricPath,
  call,
  metricType,
}: ExampleMetricProps) => {
  if (!call || !metricPath) {
    return null;
  }

  return (
    <>
      <div className="mt-4 flex items-center gap-4 text-sm text-wb-text-tertiary">
        <span>Example value:</span>
        <ExampleValue
          metricPath={metricPath}
          call={call}
          metricType={metricType}
        />
      </div>
      {metricType === 'str' && (
        <div className="mt-8">
          <Banner variant="warning">
            String values cannot be used for alerting.
          </Banner>
        </div>
      )}
    </>
  );
};
